import { Collapse, styled, Box, Button } from "@mui/material";
import MapIcon from "@mui/icons-material/Map";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import SaveTripButton from "components/cars/SaveTripButton";
import ShareTripButton from "components/cars/ShareTripButton";
import { useAppSelector } from "redux/hooks";
import {
  selectReferenceItinerary,
  selectReferenceTrip,
  selectReferenceCar,
} from "redux/slices/shed/shedSlice";

const CollapseToolbar = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "flex-start",
  width: "100%",
  marginBottom: theme.spacing(1),
}));

type Props = {
  isMinimized: boolean;
  onToggle: () => void;
  onClearTrip: () => void;
  children: React.ReactElement;
  shareRoute?: string;
  includeExperience?: boolean;
};

const MapToolbarCollapse = ({
  children,
  onClearTrip,
  isMinimized,
  onToggle,
  shareRoute,
  includeExperience,
}: Props) => {
  const itinerary = useAppSelector(selectReferenceItinerary);
  const trip = useAppSelector(selectReferenceTrip);
  const referenceCar = useAppSelector(selectReferenceCar);

  return (
    <Box
      sx={{
        flex: isMinimized ? "0 0 auto" : 1,
        minHeight: 0,
        display: "flex",
        flexDirection: "column",
      }}
    >
      <CollapseToolbar>
        <Button startIcon={<MapIcon />} onClick={onClearTrip}>
          New
        </Button>
        <SaveTripButton itinerary={itinerary} />
        <ShareTripButton
          trip={trip}
          genericVin={referenceCar?.genericVin ?? ""}
          shareRoute={shareRoute}
          includeExperience={includeExperience}
        />
        <Button
          startIcon={isMinimized ? <ExpandMoreIcon /> : <ExpandLessIcon />}
          onClick={onToggle}
          sx={{
            ml: "auto",
            color: "text.secondary",
            "& .MuiButton-startIcon": {
              color: "text.disabled",
            },
            "&:hover": {
              color: "text.primary",
              backgroundColor: "action.hover",
            },
          }}
        >
          {isMinimized ? "View trip" : "Hide trip"}
        </Button>
      </CollapseToolbar>
      <Collapse
        in={!isMinimized}
        unmountOnExit
        sx={{
          flex: isMinimized ? "0 0 auto" : "1 1 0%",
          minHeight: 0,
          overflow: "hidden",
          // Only force full height while open — otherwise Hide leaves a blank flex hole.
          ...(!isMinimized && {
            "& .MuiCollapse-wrapper": {
              height: "100% !important",
              minHeight: 0,
            },
            "& .MuiCollapse-wrapperInner": {
              height: "100%",
              minHeight: 0,
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
            },
          }),
        }}
      >
        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            overflowY: "auto",
            WebkitOverflowScrolling: "touch",
          }}
        >
          {children}
        </Box>
      </Collapse>
    </Box>
  );
};

export default MapToolbarCollapse;
